import React, { memo } from "react";

import Key from "./Key";
import { KeySeparator } from "./components/KeySeparator";
import { KeyStackProps } from "./interfaces";

/**
 * KeySequence component that displays a sequence of keys with "Then" as a separator.
 *
 * @component
 * @param props - The props for the KeySequence component.
 * @param props.keys - An array of key objects, each containing a type and value.
 * @param props.status - The status of the keys, which determines their styling.
 * @param props.isWhite - Whether the keys are white.
 * @example
 * // Example usage of the KeySequence component
 * import { KeySequence } from './KeySequence';
 *
 * const App = () => {
 *   const keys = [
 *     { type: 'text', value: 'ctrl' },
 *     { type: 'letter', value: '⌘' },
 *     { type: 'letter', value: 'K' },
 *   ];
 *
 *   return (
 *     <div>
 *       <KeySequence keys={keys} status="active" isWhite />
 *     </div>
 *   );
 * };
 *
 * export default App;
 */

const KeySequence = memo(({ keys, status, isWhite }: KeyStackProps) => {
  return (
    <div
      className="flex items-center"
      role="group"
      aria-label="Keyboard shortcut sequence"
    >
      {keys.map((keyItem, index) => (
        <React.Fragment key={keyItem.value}>
          <Key
            type={keyItem.type as "text"}
            value={keyItem.value}
            status={status}
            isWhite={isWhite}
          />
          {index < keys.length - 1 && <KeySeparator />}
        </React.Fragment>
      ))}
    </div>
  );
});

KeySequence.displayName = "KeySequence";

export default KeySequence;
